/**
 * Supervisor manual checklist (PRD_NOTETAKING § Manual Supervision Checklist).
 */
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  buildNoteRiskContext,
  hasActionableRiskFactorSelection,
  hasSelectedRiskFactor,
  noteMentionsChildOrElderAbuse,
  noteMentionsHomicidalIdeation,
  noteMentionsIntimatePartnerViolence,
  noteMentionsSafetyPlan,
  noteMentionsSelfHarm,
  noteMentionsSubstanceUse,
  noteMentionsSuicidalIdeation,
  type NoteRiskContext,
} from './utils/noteRiskContext';

export type ManualSupervisionCheckId =
  | 'risk-assessment-completed'
  | 'increased-substance-use'
  | 'self-harm'
  | 'suicidal-ideation'
  | 'homicidal-ideation'
  | 'child-elder-abuse'
  | 'intimate-partner-violence'
  | 'safety-plan-review';

export interface ManualSupervisionCheckItem {
  id: ManualSupervisionCheckId;
  label: string;
}

/** PRD order — risk-factor coverage items only. */
export const MANUAL_SUPERVISION_CHECK_ITEMS: ManualSupervisionCheckItem[] = [
  { id: 'risk-assessment-completed', label: 'Risk assessment completed' },
  { id: 'increased-substance-use', label: 'Increased substance use addressed' },
  { id: 'self-harm', label: 'Self-harm addressed' },
  { id: 'suicidal-ideation', label: 'Suicidal ideation addressed' },
  { id: 'homicidal-ideation', label: 'Homicidal ideation addressed' },
  { id: 'child-elder-abuse', label: 'Child or elder abuse addressed' },
  { id: 'intimate-partner-violence', label: 'Intimate partner violence addressed' },
  { id: 'safety-plan-review', label: 'Safety plan review completed' },
];

export function isManualSupervisionCheckVisible(
  id: ManualSupervisionCheckId,
  ctx: NoteRiskContext,
): boolean {
  const text = ctx.clinicalText;
  switch (id) {
    case 'risk-assessment-completed':
      return true;
    case 'increased-substance-use':
      return noteMentionsSubstanceUse(text);
    case 'self-harm':
      return noteMentionsSelfHarm(text);
    case 'suicidal-ideation':
      return hasSelectedRiskFactor(ctx, 'suicidal-ideation') || noteMentionsSuicidalIdeation(text);
    case 'homicidal-ideation':
      return hasSelectedRiskFactor(ctx, 'homicidal-ideation') || noteMentionsHomicidalIdeation(text);
    case 'child-elder-abuse':
      return hasSelectedRiskFactor(ctx, 'child-or-elder-abuse') || noteMentionsChildOrElderAbuse(text);
    case 'intimate-partner-violence':
      return noteMentionsIntimatePartnerViolence(text);
    case 'safety-plan-review':
      return (
        hasSelectedRiskFactor(ctx, 'suicidal-ideation') ||
        hasSelectedRiskFactor(ctx, 'homicidal-ideation') ||
        hasSelectedRiskFactor(ctx, 'child-or-elder-abuse') ||
        noteMentionsSafetyPlan(text) ||
        noteMentionsSuicidalIdeation(text) ||
        noteMentionsHomicidalIdeation(text)
      );
    default:
      return false;
  }
}

export function getVisibleManualSupervisionChecks(
  response: QuestionnaireResponse | null | undefined,
): ManualSupervisionCheckItem[] {
  const ctx = buildNoteRiskContext(response);
  return MANUAL_SUPERVISION_CHECK_ITEMS.filter((item) => isManualSupervisionCheckVisible(item.id, ctx));
}

/** Used when persisting supervisor acknowledgments — true if note has any non-none risk selection. */
export { hasActionableRiskFactorSelection };
